import { FunctionComponent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";

import { getSuggestedCopywritingInstruction } from "@signalist/shared-types";

import { Icon } from "@/components/Icon/Icon";
import { Tooltip } from "@/components/Tooltip/Tooltip";

import { useAppSelector } from "@/redux/hooks";

import { useUpdateListOutreachConfigMutation } from "@/pages/Private/redux/list/list-outreach.api";
import { listOutreachConfigSelector } from "@/pages/Private/redux/list/list-outreach.slice";

import { OutreachTemplateScopeArgs } from "@/pages/Private/redux/list/list-outreach-template.api";

import { EventOutreachStrip } from "../../../components/EventOutreach/EventOutreachStrip";
import { EventFormPayload } from "../../../components/EventOutreach/EventDetailsModal";
import { ColumnConfig } from "../../../schema/columnConfig";
import { EventOutreachSchemaType } from "../../../schema/list";
import { isEventOutreachEligible } from "../utils/eventOutreachEligibility";
import {
	dismissInstructionPrefill,
	isInstructionPrefillDismissed,
} from "../utils/instructionPrefill";
import { getVariableGroups } from "../utils/outreachVariables";
import { InstructionEditor, InstructionPatch } from "./InstructionEditor";
import { OutreachSection } from "./OutreachSection";

interface GeneralSectionProps {
	listId: number;
	sourceType: string;
	event?: EventOutreachSchemaType | null;
	/** The list's uploaded column configs — they become `{column_<id>_<name>}` variables. */
	listColumns?: ColumnConfig[];
	/** Increment to open the event-details modal from the create-campaign "Event" branch. */
	eventOpenSignal: number;
	onEventSaved: (payload: EventFormPayload) => void;
	onEventModalClosed: () => void;
	portalContainer?: HTMLElement | null;
}

/**
 * The General section of the Outreach step (PREC-3687 §4.1): everything both send paths read —
 * the event outreach strip and the shared copywriting instruction.
 *
 * It renders its own `OutreachSection` box (as `CampaignsSection` does) so the page stays pure
 * composition and its own line budget stays free.
 */
export const GeneralSection: FunctionComponent<GeneralSectionProps> = ({
	listId,
	sourceType,
	event,
	listColumns,
	eventOpenSignal,
	onEventSaved,
	onEventModalClosed,
	portalContainer,
}) => {
	const { t } = useTranslation();
	const tg = (key: string) => t(`list.outreach.general.${key}`);

	const config = useAppSelector(listOutreachConfigSelector(listId));
	const [updateOutreachConfig] = useUpdateListOutreachConfigMutation();

	/**
	 * The built-in instruction for this source type, offered over an empty box until the user
	 * adopts it or clicks it away (`instructionPrefill.ts`). Nothing about it is stored on the list,
	 * so the dismissal is read once per mount from this browser's `localStorage`.
	 */
	const suggestion = getSuggestedCopywritingInstruction(sourceType);
	const [isPrefillDismissed, setIsPrefillDismissed] = useState(() =>
		isInstructionPrefillDismissed(listId)
	);

	const handleDismissPrefill = () => {
		dismissInstructionPrefill(listId);
		setIsPrefillDismissed(true);
	};

	// The catalogue is the campaign editor's own (PREC-3687): the list's uploaded columns and its
	// event row are what make `{column_<id>_<name>}` and the `{event_*}` tokens exist.
	const variableGroups = useMemo(() => getVariableGroups(listColumns, event), [listColumns, event]);

	// The COPYWRITING library is scoped to this list's source type (D12) — a template saved on an
	// XLS list is offered on other XLS lists, and nowhere else.
	const scope: OutreachTemplateScopeArgs = useMemo(
		() => ({ kind: "COPYWRITING", listSourceType: sourceType }),
		[sourceType]
	);

	const handlePersist = async (patch: InstructionPatch) => {
		try {
			await updateOutreachConfig({
				listId,
				body: {
					...(patch.instruction === undefined ? {} : { copywritingInstruction: patch.instruction }),
					...(patch.templateId === undefined
						? {}
						: { copywritingInstructionTemplateId: patch.templateId }),
				},
			}).unwrap();
		} catch {
			// Errors surfaced by the global RTK Query error middleware.
		}
	};

	return (
		<OutreachSection subtitle={tg("subtitle")} title={tg("title")}>
			{/*
			 * Two gates, and only the outer one is new: the source type must offer event outreach at
			 * all (D6), and inside, the strip keeps its own PREC-3520 rule of showing the bar only
			 * once a `list_event` row exists (D7).
			 */}
			{isEventOutreachEligible(sourceType) && (
				<EventOutreachStrip
					event={event}
					listId={listId}
					openSignal={eventOpenSignal}
					onModalClosed={onEventModalClosed}
					onSaved={onEventSaved}
				/>
			)}

			<div className="mb-3 flex items-center gap-2">
				<span className="text-sm font-semibold text-text">{tg("instruction.label")}</span>
				<span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-text-meta">
					{tg("instruction.optional")}
				</span>
				<Tooltip content={tg("instruction.help")}>
					<Icon className="text-gray-400" icon={faCircleInfo} />
				</Tooltip>
			</div>

			<InstructionEditor
				loadedTemplateId={config?.copywritingInstructionTemplateId ?? null}
				placeholder={tg("instruction.placeholder")}
				portalContainer={portalContainer}
				prefillText={isPrefillDismissed ? undefined : suggestion?.body}
				saveScopeHint={tg("saveTemplate.scopeHint")}
				scope={scope}
				value={config?.copywritingInstruction ?? ""}
				variableGroups={variableGroups}
				onDismissPrefill={handleDismissPrefill}
				onPersist={handlePersist}
			/>
		</OutreachSection>
	);
};
